<script setup>
import { defineProps, onMounted, ref } from 'vue'
import { formatDate, getDayName, getWeekInfo } from '@/mixins/global/globalPlaces'
import PlaceInformationGrid from '@/components/object_placeCollection/templates/placeInformationGrid.vue'

const props = defineProps({
    placeInformation: {
        type: Object,
        default: null
    }
})

const activeDate = ref('')
const currentWeekStart = ref(new Date())
const visibleDates = ref([])
const availableDates = ref([])
const weekDates = ref([])
const activePlaces = ref([])

function updateActivePlaces() {
    activePlaces.value = props.placeInformation[activeDate.value].places || []
}

function updateVisibleDates() {
    if (availableDates.value.length === 0) return

    const weekStart = new Date(currentWeekStart.value)
    const weekStartIso = weekStart.toISOString().split('T')[0]

    const startIndex = availableDates.value.findIndex(date => date >= weekStartIso)

    if (startIndex === -1) {
        visibleDates.value = availableDates.value.slice(0, 7)
    } else {
        visibleDates.value = availableDates.value.slice(startIndex, startIndex + 7)

        if (visibleDates.value.length < 7) {
            visibleDates.value = availableDates.value.slice(Math.max(0, availableDates.value.length - 7))
        }
    }

    if (!visibleDates.value.includes(activeDate.value) && visibleDates.value.length > 0) {
        updateActiveDate(visibleDates.value[0])
    }

    weekDates.value = visibleDates.value.length > 0 ? visibleDates.value : availableDates.value.slice(0, 7)
}

const isMobile = ref(false)

function checkMobileView() {
    isMobile.value = window.matchMedia('(max-width: 79em)').matches
}

function nextWeek() {
    if (availableDates.value.length === 0) return

    if (isMobile.value) {
        const currentActiveIndex = availableDates.value.indexOf(activeDate.value)
        if (currentActiveIndex < availableDates.value.length - 1) {
            updateActiveDate(availableDates.value[currentActiveIndex + 1])
        }
    } else {
        const currentLastDate = visibleDates.value[visibleDates.value.length - 1]
        const lastDateIndex = availableDates.value.indexOf(currentLastDate)

        if (lastDateIndex < availableDates.value.length - 1) {
            currentWeekStart.value = new Date(availableDates.value[lastDateIndex + 1])
            updateVisibleDates()

            if (visibleDates.value.length > 0) {
                updateActiveDate(visibleDates.value[0])
            }
        }
    }
}

function previousWeek() {
    if (availableDates.value.length === 0) return

    if (isMobile.value) {
        const currentActiveIndex = availableDates.value.indexOf(activeDate.value)
        if (currentActiveIndex > 0) {
            updateActiveDate(availableDates.value[currentActiveIndex - 1])
        }
    } else {
        const currentFirstDate = visibleDates.value[0]
        const firstDateIndex = availableDates.value.indexOf(currentFirstDate)

        if (firstDateIndex > 0) {
            const newStartIndex = Math.max(0, firstDateIndex - 7)
            currentWeekStart.value = new Date(availableDates.value[newStartIndex])
            updateVisibleDates()

            if (visibleDates.value.length > 0) {
                updateActiveDate(visibleDates.value[0])
            }
        }
    }
}

function updateActiveDate(dateString) {
    activeDate.value = dateString
    updateActivePlaces()

    if (isMobile.value) {
        const date = new Date(dateString)
        const dayOfWeek = date.getDay()

        const mondayDate = new Date(date)
        const difference = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        mondayDate.setDate(mondayDate.getDate() - difference)

        currentWeekStart.value = mondayDate

        updateVisibleDates()
    }
}

function isWeekTitleVisible() {
    return weekDates.value.length > 0 && !isMobile.value && weekDates.value[0] && new Date(weekDates.value[0]).getDay() === 1
}

onMounted(() => {
    checkMobileView()

    window.addEventListener('resize', checkMobileView)

    if (props.placeInformation && Object.keys(props.placeInformation).length > 0) {
        availableDates.value = Object.keys(props.placeInformation).sort()

        const today = new Date().toISOString().split('T')[0]

        const todayIndex = availableDates.value.indexOf(today)

        if (todayIndex !== -1) {
            activeDate.value = today

            const weekStartIndex = Math.floor(todayIndex / 7) * 7
            currentWeekStart.value = new Date(availableDates.value[weekStartIndex])
        } else {
            currentWeekStart.value = new Date(availableDates.value[0])
        }

        updateVisibleDates()

        if (!activeDate.value && visibleDates.value.length > 0) {
            activeDate.value = visibleDates.value[0]
        }

        updateActivePlaces()
    }
})
</script>

<template>
    <div class="week-slider-container">
        <div v-if="!placeInformation || Object.keys(placeInformation).length === 0" class="place-info-state-message">
            <div class="place-info-empty">Keine Platzinformationen verfügbar.</div>
        </div>

        <template v-else>
            <div v-if="availableDates.length > 0" class="place-info-header">
                <div class="place-info-week-title" v-if="isWeekTitleVisible()">
                    {{ getWeekInfo(weekDates) }}
                </div>

                <div class="place-info-date-navigation">
                    <button
                        @click="previousWeek"
                        class="place-info-navigation-button place-info-previous-button"
                        :disabled="isMobile
                            ? availableDates.indexOf(activeDate) === 0
                            : visibleDates[0] === availableDates[0]"
                        :title="isMobile ? 'Vorheriger Tag' : 'Vorherige Woche'"
                        :aria-label="isMobile ? 'Vorherigen Tag anzeigen' : 'Vorherige Woche anzeigen'"
                    >
                        <span class="place-info-navigation-icon">&#10094;</span>
                    </button>

                    <div class="place-info-date-tabs">
                        <template v-if="isMobile">
                            <button class="place-info-date-tab place-info-date-tab-active">
                                {{ getDayName(activeDate, true) + ', ' + formatDate(activeDate) }}
                            </button>
                        </template>
                        <template v-else>
                            <button
                                v-for="date in weekDates"
                                :key="date"
                                :class="[
                                    'place-info-date-tab',
                                    { 'place-info-date-tab-active': date === activeDate }
                                    ]"
                                @click="updateActiveDate(date)"
                            >
                                {{ getDayName(date, true) + ', ' + formatDate(date) }}
                            </button>
                        </template>
                    </div>

                    <button
                        @click="nextWeek"
                        class="place-info-navigation-button place-info-next-button"
                        :disabled="isMobile
                            ? availableDates.indexOf(activeDate) === availableDates.length - 1
                            : visibleDates[visibleDates.length-1] === availableDates[availableDates.length-1]"
                        :title="isMobile ? 'Nächster Tag' : 'Nächste Woche'"
                        :aria-label="isMobile ? 'Nächsten Tag anzeigen' : 'Nächste Woche anzeigen'"
                    >
                        <span class="place-info-navigation-icon">&#10095;</span>
                    </button>
                </div>
            </div>

            <place-information-grid v-if="activePlaces.length > 0" :places="activePlaces"/>

            <div v-else-if="availableDates.length > 0" class="place-info-state-message">
                <div class="place-info-empty">Keine Platzinformationen für das gewählte Datum verfügbar.</div>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
.place-info-header {
    border-top: 1px solid #eeeeee;
    border-bottom: 1px solid #eeeeee;
    padding: 1rem 0;
}

.place-info-week-title {
    text-align: center;
    margin-bottom: 1rem;
}

.place-info-date-navigation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
}

.place-info-navigation-button {
    background: none;
    border: 1px solid #eeeeee;
    width: 2.25em;
    height: 2.25em;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover:not(:disabled) {
        border-color: #b7b7b7;
    }

    &:disabled {
        opacity: 0;
        cursor: unset;
    }
}

.place-info-date-tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 1.5rem;
    flex: 1;
    justify-content: center;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.place-info-date-tab {
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.25s ease;

    &.place-info-date-tab-active {
        font-weight: 700;
        text-decoration: underline;
    }
}

@media (max-width: 79em) {
    .place-info-date-tabs {
        justify-content: center;
    }

    .place-info-date-tab {
        font-weight: 700;
        font-size: 1.1em;
    }

    .place-info-week-title {
        font-size: 0.9em;
    }
}

@media (max-width: 60em) {
    .place-info-date-tabs {
        justify-content: center;
    }
}
</style>
